import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Footer from '../components/Footer';
import {
  Car,
  Key,
  Truck,
  Wallet,
  Shield,
  MapPin,
  Clock,
  Star,
  Users,
  Globe,
  Coins,
  Gift,
  ArrowRight,
  CheckCircle,
  Smartphone,
  Zap,
  ShieldCheck,
  Radio,
  Share2,
  HeartHandshake,
  AlertTriangle,
  Eye
} from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, color = '#00BFA6' }) => (
  <Card className="card-elevated hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
    <CardContent className="p-6">
      <div 
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <h3 className="text-lg font-bold text-slate-800 mb-2 font-['Outfit']">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);

const StepCard = ({ number, title, description, icon: Icon }) => (
  <div className="flex flex-col items-center text-center">
    <div className="relative mb-4">
      <div className="w-16 h-16 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
        <Icon className="w-8 h-8 text-[#00BFA6]" />
      </div>
      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#FFD166] flex items-center justify-center text-slate-900 font-bold text-sm">
        {number}
      </div>
    </div>
    <h3 className="text-lg font-bold text-slate-800 mb-2 font-['Outfit']">{title}</h3>
    <p className="text-slate-600 text-sm">{description}</p>
  </div>
);

const BenefitItem = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#00BFA6]" />
    </div>
    <span className="text-slate-700">{text}</span>
  </div>
);

// Security Feature Card Component
const SecurityFeatureCard = ({ icon: Icon, title, description }) => (
  <div className="group p-6 bg-white rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-teal-500/20">
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="text-lg font-bold text-slate-800 mb-2 font-['Outfit']">{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
  </div>
);

// Trust Badge Component (Reusable)
const TrustBadge = ({ size = 'default' }) => {
  const sizeClasses = size === 'large' 
    ? 'px-6 py-4 gap-4' 
    : 'px-4 py-2 gap-2';
  const iconSize = size === 'large' ? 'w-10 h-10' : 'w-6 h-6';
  const textSize = size === 'large' ? 'text-lg' : 'text-sm';
  
  return (
    <div className={`inline-flex items-center ${sizeClasses} bg-gradient-to-r from-teal-500 to-teal-600 rounded-full shadow-lg shadow-teal-500/30`}>
      <div className={`${iconSize} rounded-full bg-white/20 flex items-center justify-center`}>
        <ShieldCheck className={`${size === 'large' ? 'w-6 h-6' : 'w-4 h-4'} text-white`} />
      </div>
      <span className={`${textSize} font-bold text-white`}>Certificado de Confianza Mercapoli</span>
    </div>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 gradient-hero">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Move Everything.
                <span className="text-gradient-teal block">Move Smarter.</span>
              </h1>
              <p className="text-xl text-slate-600 mt-6 leading-relaxed">
                Movilidad, renta de vehículos y logística en una sola super-app. 
                Viaja, renta y envía con la confianza del ecosistema Mercapoli.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/register">
                  <Button className="btn-primary text-lg h-14 px-8 w-full sm:w-auto">
                    Comenzar Ahora
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/explore">
                  <Button variant="outline" className="h-14 px-8 border-[#00BFA6] text-[#00BFA6] hover:bg-[#00BFA6]/10 rounded-full w-full sm:w-auto">
                    Ver Cómo Funciona
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-8 mt-10">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  <span className="text-slate-600 text-sm">Sin compromisos</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  <span className="text-slate-600 text-sm">Paga con DIGGII</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  <span className="text-slate-600 text-sm">100% Seguro</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop" 
                  alt="Mercapoli Move App"
                  className="rounded-3xl shadow-2xl shadow-slate-300/50"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FFD166] rounded-full opacity-20 blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#00BFA6] rounded-full opacity-20 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECURITY CORE SECTION ============ */}
      <section id="security-core" className="py-20 px-4 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-6">
              <Shield className="w-5 h-5 text-teal-600" />
              <span className="text-teal-700 font-medium text-sm">Seguridad como Core Business</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight max-w-4xl mx-auto">
              La seguridad no es un extra.
              <span className="text-teal-600 block mt-2">Es parte de nuestro Core Business.</span>
            </h2>
            
            <p className="text-lg sm:text-xl text-slate-600 mt-6 max-w-3xl mx-auto leading-relaxed">
              En Mercapoli Move, cada viaje puede ser acompañado, monitoreado y respaldado por personas reales, 24/7.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Text Content */}
            <div className="space-y-6">
              <p className="text-slate-600 text-lg leading-relaxed">
                Diseñamos Mercapoli Move desde el primer día con una idea clara: 
                <strong className="text-slate-800"> si una persona confía su trayecto, su envío o su movilidad a nuestra plataforma, la seguridad no puede ser opcional.</strong>
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                Por eso contamos con una <span className="text-teal-600 font-semibold">Central de Monitoreo activa</span>, operadores humanos, herramientas de emergencia y seguimiento en tiempo real para que <strong className="text-slate-800">nunca viajes solo</strong>.
              </p>
              
              {/* Trust Badge */}
              <div className="pt-4">
                <TrustBadge size="large" />
                <p className="text-slate-500 text-sm mt-3 ml-1">
                  Este servicio cuenta con monitoreo activo, herramientas de emergencia y acompañamiento humano en tiempo real.
                </p>
              </div>
            </div>

            {/* Right: Visual */}
            <div className="relative">
              <div className="relative z-10 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 shadow-2xl">
                {/* Monitoring Console Preview */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-400 text-sm font-medium">Central de Monitoreo Activa</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                        <Radio className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Operadores en línea</p>
                        <p className="text-slate-400 text-xs">Disponibles 24/7</p>
                      </div>
                    </div>
                    <span className="text-teal-400 font-bold">ACTIVO</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Viajes monitoreados</p>
                        <p className="text-slate-400 text-xs">En tiempo real</p>
                      </div>
                    </div>
                    <span className="text-amber-400 font-bold">247</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Viajes seguros hoy</p>
                        <p className="text-slate-400 text-xs">Sin incidentes</p>
                      </div>
                    </div>
                    <span className="text-green-400 font-bold">1,248</span>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-teal-500/10 border border-teal-500/20 rounded-xl">
                  <p className="text-teal-300 text-sm text-center">
                    "Este viaje está siendo monitoreado por la Central de Seguridad Mercapoli Move 24/7"
                  </p>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-500 rounded-full opacity-20 blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-amber-500 rounded-full opacity-20 blur-2xl" />
            </div>
          </div>

          {/* Security Features Grid */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] text-center mb-10">
              ¿Qué nos hace diferentes?
            </h3>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <SecurityFeatureCard
                icon={Radio}
                title="Central de Monitoreo 24/7"
                description="Operadores reales supervisan alertas, viajes y emergencias en tiempo real."
              />
              <SecurityFeatureCard
                icon={AlertTriangle}
                title="Botón de Pánico Real"
                description="No es decorativo. Activa protocolos, operadores y seguimiento inmediato."
              />
              <SecurityFeatureCard
                icon={Share2}
                title="Compartir Viaje en Tiempo Real"
                description="Comparte tu trayecto con familiares o amigos mediante un enlace seguro, sin necesidad de cuenta."
              />
              <SecurityFeatureCard
                icon={HeartHandshake}
                title="Acompañamiento Humano"
                description="Personas que responden, escuchan y actúan cuando más se necesita."
              />
            </div>
          </div>

          {/* Closing Message */}
          <div className="text-center bg-gradient-to-r from-teal-50 to-slate-50 rounded-3xl p-10 border border-teal-100">
            <p className="text-xl sm:text-2xl text-slate-700 font-medium leading-relaxed max-w-3xl mx-auto">
              "Si algo no se siente seguro, no debería existir.
              <span className="text-teal-600 block mt-2">
                Por eso en Mercapoli Move, la seguridad no es una promesa: es una operación activa."
              </span>
            </p>
            
            <Link to="/register">
              <Button className="mt-8 h-14 px-10 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-lg rounded-full shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 transition-all duration-300">
                <ShieldCheck className="w-5 h-5 mr-2" />
                Viaja con confianza
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit']">
              Todo lo que necesitas en una app
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Mercapoli Move integra los mejores servicios de movilidad y logística para tu día a día.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={Car}
              title="Ride-Hailing"
              description="Solicita un viaje en segundos. Conductores verificados, precios transparentes y tracking en tiempo real."
              color="#00BFA6"
            />
            <FeatureCard
              icon={Key}
              title="Renta de Vehículos"
              description="Alquila por hora, día o semana. Autos de particulares y flotas profesionales disponibles."
              color="#00887A"
            />
            <FeatureCard
              icon={Truck}
              title="Fletes y Mandados"
              description="Desde un documento hasta mudanzas completas. Logística última milla confiable."
              color="#FFD166"
            />
            <FeatureCard
              icon={Wallet}
              title="Digi Wallet"
              description="Paga con tu saldo o tokens DIGGII. Gana cashback y bonos por referidos."
              color="#00BFA6"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit']">
              ¿Cómo funciona?
            </h2>
            <p className="text-slate-600 mt-4">
              En tres simples pasos estarás disfrutando de todos nuestros servicios.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <StepCard
              number={1}
              icon={Smartphone}
              title="Descarga la App"
              description="Disponible para iOS y Android. Regístrate con tu email o número de teléfono."
            />
            <StepCard
              number={2}
              icon={MapPin}
              title="Elige tu Servicio"
              description="Viajes, rentas o envíos. Selecciona origen, destino y confirma tu solicitud."
            />
            <StepCard
              number={3}
              icon={Zap}
              title="¡Listo!"
              description="Sigue tu servicio en tiempo real y paga de forma segura con Digi Wallet."
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit'] mb-8">
                ¿Por qué elegir Mercapoli Move?
              </h2>
              <div className="space-y-5">
                <BenefitItem icon={Shield} text="Botón de pánico y seguimiento GPS 24/7" />
                <BenefitItem icon={Clock} text="Servicios disponibles las 24 horas" />
                <BenefitItem icon={Star} text="Conductores y vehículos verificados" />
                <BenefitItem icon={Coins} text="Gana tokens DIGGII con cada servicio" />
                <BenefitItem icon={Gift} text="Bonos por referir amigos y familiares" />
                <BenefitItem icon={Globe} text="Disponible en múltiples países" />
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop" 
                alt="Seguridad Mercapoli"
                className="rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DIGGII Ecosystem Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#00BFA6]/5 to-[#00887A]/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <Card className="card-elevated">
                  <CardContent className="p-6 text-center">
                    <Coins className="w-10 h-10 text-[#FFD166] mx-auto mb-3" />
                    <h4 className="font-bold text-slate-800">1 DIGGII = 1 USD</h4>
                    <p className="text-slate-500 text-sm mt-1">Valor estable garantizado</p>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="p-6 text-center">
                    <Gift className="w-10 h-10 text-[#00BFA6] mx-auto mb-3" />
                    <h4 className="font-bold text-slate-800">Referidos</h4>
                    <p className="text-slate-500 text-sm mt-1">Gana DIGGII por invitar</p>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="p-6 text-center">
                    <Star className="w-10 h-10 text-[#FFD166] mx-auto mb-3" />
                    <h4 className="font-bold text-slate-800">Cashback</h4>
                    <p className="text-slate-500 text-sm mt-1">En cada servicio</p>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="p-6 text-center">
                    <Wallet className="w-10 h-10 text-[#00BFA6] mx-auto mb-3" />
                    <h4 className="font-bold text-slate-800">Digi Wallet</h4>
                    <p className="text-slate-500 text-sm mt-1">Pagos instantáneos</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit'] mb-6">
                Ecosistema <span className="text-gradient-teal">DIGGII</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                DIGGII es el token del ecosistema Mercapoli. Úsalo para pagar servicios, 
                recibir bonos y disfrutar de beneficios exclusivos en toda la plataforma.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  Gana DIGGII por cada viaje, renta o envío
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  Paga parcial o totalmente con tokens
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  Invita amigos y gana recompensas progresivas
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6]" />
                  Compatible con Mercapoli Delivery
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#00BFA6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-6">
            Únete a Mercapoli Move
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Más de 10,000 usuarios ya disfrutan de la mejor experiencia de movilidad y logística.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-white text-[#00BFA6] hover:bg-white/90 rounded-full h-14 px-8 text-lg font-bold shadow-lg">
                Crear Cuenta Gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-center gap-8 mt-10">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">10K+</p>
              <p className="text-white/80 text-sm">Usuarios activos</p>
            </div>
            <div className="w-px h-12 bg-white/30" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">50K+</p>
              <p className="text-white/80 text-sm">Viajes completados</p>
            </div>
            <div className="w-px h-12 bg-white/30" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">5</p>
              <p className="text-white/80 text-sm">Países</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
